import type { AdditionalFieldType } from "@/types/additionalFields";
import type { AdditionalFieldItem } from "@/features/accountSettings/types/additionalFields";

/**
 * フィールドの重複バリデーション結果
 */
export interface FieldValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * フィールド名と型の重複をチェックする関数
 *
 * 重複チェックの挙動:
 * 1. 同じ名前・同じ型の削除済み項目がある → 復元を促す
 * 2. 同じ名前・同じ型の有効な項目がある → 重複エラー
 * 3. それ以外 → バリデーションOK
 *
 * @param fields - 全フィールドのリスト
 * @param label - チェック対象の項目名
 * @param fieldType - チェック対象の型
 * @param excludeFieldId - 除外するフィールドID（編集時に自分自身を除外）
 * @returns バリデーション結果
 */
export const validateFieldDuplication = (
  fields: AdditionalFieldItem[] | undefined,
  label: string,
  fieldType: AdditionalFieldType,
  excludeFieldId?: string
): FieldValidationResult => {
  if (!fields) {
    return { isValid: true };
  }

  // 1. 同じ名前・同じ型の削除済み項目があるかチェック
  const deletedSameField = fields.find(
    (field) =>
      field.id !== excludeFieldId &&
      field.key === label &&
      field.fieldType === fieldType &&
      !!field.deletedAt
  );

  if (deletedSameField) {
    return {
      isValid: false,
      errorMessage:
        "この項目名と型の組み合わせは削除済み項目に存在します。削除済み項目から復元してください。",
    };
  }

  // 2. 同じ名前・同じ型の有効な項目があるかチェック
  const existingSameField = fields.find(
    (field) =>
      field.id !== excludeFieldId &&
      field.key === label &&
      field.fieldType === fieldType &&
      !field.deletedAt
  );

  if (existingSameField) {
    return {
      isValid: false,
      errorMessage: "この項目名と型の組み合わせは既に存在します",
    };
  }

  // 3. それ以外 → OK
  return { isValid: true };
};
